import React, { ChangeEvent, useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Select, ISelectProps } from 'transfix-ui/components/Select'
import { TextField } from 'transfix-ui/components/TextField'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { ISubmitBrokerCarrierAgreementPayload } from 'types'
import * as yup from 'yup'
import { TBcaSteps } from 'apps/fleet-planner/components/activation/types'
import { useActivationContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { postMessageToMobileApp } from 'utils/mobileApp'
import {
  usStatesOptions,
  countriesOptions,
  COUNTRIES,
  canadianStatesOptions,
} from 'constants/countriesStates'
import { carrierEntities } from 'constants/entities'
import { AgreementTerms } from './AgreementTerms'
import { EActivationStep, EMobileMessage } from './constants'

export const BrokerCarrierAgreementSchema = yup
  .object({
    entityType: yup.string().required('Please select an entity type.'),
    ein: yup.string().required('Please enter a EIN number.').min(9, 'Must be 9 digits.'),
    address: yup.string().required('Please enter billing address.'),
    countryCode: yup.string().required('Please select country.'),
    city: yup.string().required('Please enter city.'),
    zipcode: yup.string().when('countryCode', {
      is: val => val === COUNTRIES[1].code, // 'CA'
      then: yup
        .string()
        .required('Please enter postal code.')
        .min(6, 'Must be 6 or 7 characters.')
        .max(7, 'Must be 6 or 7 characters.')
        .matches(/^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/, 'Invalid postal code.'),
      otherwise: yup
        .string()
        .required('please enter ZIP code.')
        .min(5, 'Must be 5 digits.')
        .max(5, 'Must be 5 digits.'),
    }),
    stateCode: yup.string().required('Please enter state.'),
    contactEmail: yup
      .string()
      .trim()
      .email('Please enter a valid email.')
      .required('Please enter email.'),
    signatory: yup.string().required('Please sign your full name.'),
    contactPhone: yup
      .string()
      .required('Please enter phone number.')
      .min(10, 'Please enter a valid phone number.'),
  })
  .required()

export type BrokerCarrierAgreementSchemaType = yup.InferType<typeof BrokerCarrierAgreementSchema>

export const getBcaPayload = (
  data: BrokerCarrierAgreementSchemaType,
  locationId?: number
): ISubmitBrokerCarrierAgreementPayload => {
  const {
    address,
    city,
    contactEmail,
    contactPhone,
    countryCode,
    ein,
    entityType,
    signatory,
    stateCode,
    zipcode,
  } = data || {}
  return {
    carrierApplication: {
      ein,
      entityType,
      contactEmail,
      locationAttributes: {
        ...(locationId && { id: locationId }),
        address,
        city,
        stateCode,
        countryCode,
        contactEmail: contactEmail.trim(),
        contactPhone,
        zipcode: countryCode === 'US' ? zipcode : zipcode.replace(' ', '').toUpperCase(),
      },
    },
    brokerCarrierAgreement: {
      signatory,
    },
  }
}

const useBCAStyles = makeStyles({
  divider: {
    width: '100%',
    borderBottom: `1px solid ${brandColors.coolGray3}`,
    margin: '16px 0',
  },
})

export interface IBrokerCarrierAgreement {
  /**
   * @note optional prop; only used for fmsBcaFormSplit feature
   */
  bcaPart?: number
  /**
   * @note optional prop; only used for fmsBcaFormSplit feature
   */
  setBcaPart?: (value: TBcaSteps) => void
}

export const DeprecatedBrokerCarrierAgreementForm = ({
  bcaPart = 1,
  setBcaPart = () => null,
}: IBrokerCarrierAgreement) => {
  const { trackEvent } = useFMSAnalytics()

  useEffect(() => {
    trackEvent('Setup Guide', 'VIEWED_BCA')
  }, [trackEvent])

  const {
    carrierDetails,
    errors,
    isLoading,
    mobileApp,
    setCurrentStep,
    submitBca,
  } = useActivationContext()
  const { id, location } = carrierDetails || {}
  const { id: locationId } = location || {}
  const { control, errors: formErrors, handleSubmit, trigger } = useForm({
    mode: 'all',
    resolver: yupResolver(BrokerCarrierAgreementSchema),
    defaultValues: BrokerCarrierAgreementSchema.cast({
      entityType: '',
      ein: '',
      address: '',
      city: '',
      stateCode: '',
      zipcode: '',
      countryCode: COUNTRIES[0].code,
      contactEmail: '',
      signatory: '',
      contactPhone: '',
    }),
  })

  const submitHandler = async (data: BrokerCarrierAgreementSchemaType) => {
    const payload = getBcaPayload(data, locationId)
    const { id: responseId } = (await submitBca(id, payload)) || {}
    if (responseId) {
      trackEvent('Setup Guide', 'SUBMIT_BCA_SUCCESS')
      postMessageToMobileApp(mobileApp, EMobileMessage.COMPANY_INFO_COLLECTED)
      setCurrentStep(EActivationStep.documents)
    } else {
      trackEvent('Setup Guide', 'SUBMIT_BCA_FAILURE')
    }
  }

  const continueHandler = () => {
    trigger().then(isValid => {
      if (isValid) {
        setBcaPart(2)
      }
    })
  }

  const backHandler = () => {
    setBcaPart(1)
  }

  const agreement = {
    ein: useWatch<BrokerCarrierAgreementSchemaType['ein']>({
      name: 'ein',
      control,
      defaultValue: '',
    }),
    entity: useWatch<BrokerCarrierAgreementSchemaType['entityType']>({
      name: 'entityType',
      control,
      defaultValue: '',
    }),
    signature: useWatch<BrokerCarrierAgreementSchemaType['signatory']>({
      name: 'signatory',
      control,
      defaultValue: '',
    }),
  }

  const currentCountry = useWatch({ name: 'countryCode', defaultValue: COUNTRIES[0].code, control })
  const isCountryUS = currentCountry === COUNTRIES[0].code
  const stateOptions = isCountryUS ? usStatesOptions : canadianStatesOptions

  const classes = useBCAStyles()

  return (
    <div>
      <Grid container alignItems='center' justifyContent='center'>
        <Grid item xs={12} md={8} lg={6}>
          <form onSubmit={handleSubmit(submitHandler)}>
            <div>
              <div style={bcaPart === 2 ? { display: 'none' } : {}}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant='h3'>Enter Company Info</Typography>
                    <Typography>
                      Please use the carrier’s legal information to fill out the Broker-Carrier
                      Agreement:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name='entityType'
                      control={control}
                      render={({ onChange, value }) => {
                        const changeHandler: ISelectProps['onChange'] = event => {
                          onChange(event.target.value)
                        }
                        return (
                          <Select
                            value={value}
                            label='Entity Type'
                            options={carrierEntities}
                            onChange={changeHandler}
                            error={!!formErrors?.entityType}
                            validationMessage={formErrors?.entityType?.message}
                            placeholder='Select an entity type'
                            dataTest='input-entity-type'
                          />
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name='ein'
                      control={control}
                      render={({ onChange, value }) => {
                        const changeHandler = ({
                          target: { value: val = '' },
                        }: ChangeEvent<HTMLInputElement>) => {
                          // limits input to 9 characters.
                          // this is neccessary because inputProps={{ maxLength: 9 }} doesn't work when type='number'
                          const newVal = val.toString().slice(0, 9)
                          onChange(newVal)
                        }
                        return (
                          <TextField
                            value={value}
                            onChange={changeHandler}
                            type='number'
                            label='EIN'
                            error={!!formErrors?.ein}
                            validationMessage={formErrors?.ein?.message}
                            placeholder='Enter your EIN number'
                            dataTest='ein-input'
                          />
                        )
                      }}
                    />
                  </Grid>
                  <div className={classes.divider} />
                  <Grid item xs={12} md={12}>
                    <Controller
                      name='address'
                      control={control}
                      render={({ onChange, value }) => {
                        const changeHandler = ({
                          target: { value: val = '' },
                        }: ChangeEvent<HTMLInputElement>) => {
                          onChange(val)
                        }
                        return (
                          <TextField
                            value={value}
                            onChange={changeHandler}
                            label='Billing Address'
                            error={!!formErrors?.address}
                            validationMessage={formErrors?.address?.message}
                            placeholder={`Enter your company's billing address`}
                            dataTest='billing-address-input'
                          />
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Controller
                      name='countryCode'
                      control={control}
                      render={({ onChange, value }) => {
                        const changeHandler: ISelectProps['onChange'] = event => {
                          onChange(event.target.value)
                        }
                        return (
                          <Select
                            value={value}
                            label='Country'
                            options={countriesOptions}
                            onChange={changeHandler}
                            error={!!formErrors?.countryCode}
                            validationMessage={formErrors?.countryCode?.message}
                          />
                        )
                      }}
                    />
                  </Grid>
                  {!isCountryUS && (
                    <Grid item xs={12} md={2}>
                      <Controller
                        name='stateCode'
                        control={control}
                        render={({ onChange, value }) => {
                          const changeHandler: ISelectProps['onChange'] = event => {
                            onChange(event.target.value)
                          }
                          return (
                            <Select
                              value={value}
                              label='Province'
                              options={stateOptions}
                              onChange={changeHandler}
                              error={!!formErrors?.stateCode}
                              validationMessage={formErrors?.stateCode?.message}
                              placeholder={isCountryUS ? 'Select a state' : 'Select a province'}
                              dataTest='state-input'
                            />
                          )
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Controller
                      name='city'
                      control={control}
                      render={({ onChange, value }) => {
                        const changeHandler = ({
                          target: { value: val = '' },
                        }: ChangeEvent<HTMLInputElement>) => {
                          onChange(val)
                        }
                        return (
                          <TextField
                            value={value}
                            onChange={changeHandler}
                            label='City'
                            error={!!formErrors?.city}
                            validationMessage={formErrors?.city?.message}
                            dataTest='city-input'
                          />
                        )
                      }}
                    />
                  </Grid>
                  {isCountryUS && (
                    <Grid item xs={6} md={2}>
                      <Controller
                        name='stateCode'
                        control={control}
                        render={({ onChange, value }) => {
                          const changeHandler: ISelectProps['onChange'] = event => {
                            onChange(event.target.value)
                          }
                          return (
                            <Select
                              value={value}
                              label='State'
                              options={stateOptions}
                              onChange={changeHandler}
                              error={!!formErrors?.stateCode}
                              validationMessage={formErrors?.stateCode?.message}
                              placeholder={isCountryUS ? 'Select a state' : 'Select a province'}
                              dataTest='state-input'
                            />
                          )
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={isCountryUS ? 6 : 12} md={2}>
                    <Controller
                      name='zipcode'
                      control={control}
                      render={({ onChange, value }) => {
                        const changeHandler = ({
                          target: { value: val = '' },
                        }: ChangeEvent<HTMLInputElement>) => {
                          onChange(val)
                        }
                        return (
                          <TextField
                            value={value}
                            onChange={changeHandler}
                            label={isCountryUS ? 'ZIP' : 'Postal Code'}
                            error={!!formErrors?.zipcode}
                            validationMessage={formErrors?.zipcode?.message}
                            dataTest='zipcode-input'
                            type={isCountryUS ? 'number' : 'text'}
                          />
                        )
                      }}
                    />
                  </Grid>
                  <div className={classes.divider} />
                  <Grid item xs={12} md={6}>
                    <Controller
                      name='contactEmail'
                      control={control}
                      render={({ onChange, value }) => {
                        const changeHandler = ({
                          target: { value: val = '' },
                        }: ChangeEvent<HTMLInputElement>) => {
                          onChange(val)
                        }
                        return (
                          <TextField
                            value={value}
                            onChange={changeHandler}
                            label='Carrier Contact Email'
                            error={!!formErrors?.contactEmail}
                            validationMessage={formErrors?.contactEmail?.message}
                            placeholder='you@yourcompany.com'
                            dataTest='carrier-contact-email-input'
                            className='fs-mask'
                          />
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ControlledPhoneInput
                      control={control}
                      name='contactPhone'
                      PhoneInputProps={{
                        placeholder: '(555) 123-4567',
                        label: 'Phone',
                        invalid: !!formErrors.contactPhone,
                        validationMessage: formErrors?.contactPhone?.message,
                        dataTest: 'contact-phone-input',
                        className: 'fs-mask',
                      }}
                    />
                  </Grid>
                  <Box pt={2} width='100%'>
                    <Grid container justifyContent='flex-end'>
                      <Grid item xs={12} md={4}>
                        <Button
                          data-test='continue-bca-button'
                          fullWidth
                          size='large'
                          onClick={continueHandler}>
                          Continue
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </div>
              {bcaPart === 2 && (
                <div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8} lg={6}>
                      <Typography variant='h3'>Read & Sign Agreement</Typography>
                    </Grid>
                    <Box mt={4}>
                      <Grid container alignItems='center' justifyContent='center'>
                        <Grid item xs={12}>
                          <AgreementTerms agreement={agreement} carrierDetails={carrierDetails} />
                        </Grid>
                      </Grid>
                    </Box>
                    <Box mt={2}>
                      <Grid item xs={12} md={12}>
                        <Controller
                          name='signatory'
                          control={control}
                          render={({ onChange, value }) => {
                            const changeHandler = ({
                              target: { value: val = '' },
                            }: ChangeEvent<HTMLInputElement>) => {
                              onChange(val)
                            }
                            return (
                              <TextField
                                value={value}
                                onChange={changeHandler}
                                label='Digital Signature'
                                helperMessage='This will serve as a Carrier Representative signature.'
                                error={!!formErrors?.signatory}
                                validationMessage={formErrors?.signatory?.message}
                                dataTest='signature-input'
                              />
                            )
                          }}
                        />
                      </Grid>
                    </Box>
                    <Grid item xs={12}>
                      {errors && (
                        <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={errors} />
                      )}
                    </Grid>

                    <Box pt={2} width='100%'>
                      <Grid container justifyContent='flex-end'>
                        <Grid item xs={12} md={4}>
                          <Button
                            disabled={isLoading}
                            data-test='submit-bca-button'
                            fullWidth
                            size='large'
                            type='submit'>
                            {isLoading ? 'Loading...' : 'Sign and Continue'}
                          </Button>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Button
                            data-test='go-back-bca-button'
                            color='teritary'
                            fullWidth
                            size='large'
                            onClick={backHandler}>
                            Go back
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </div>
              )}
            </div>
          </form>
        </Grid>
      </Grid>
    </div>
  )
}
